import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { getProfile, type VisitRecord } from '@/lib/api';
import { Appointment, Prescription } from '@/types';

// Extended Patient interface for comprehensive patient management
interface Patient {
  id: string;
  name: string;
  age?: number;
  gender?: 'Male' | 'Female' | 'Other';
  phone?: string;
  phone_2?: string; // Secondary phone number
  email?: string;
  address?: string;
  city?: string; // City/District
  pin_code?: string; // Postal/PIN code
  preferred_language?: string; // Communication language
  aadhar_number?: string; // Aadhar/National ID
  blood_group?: 'A+' | 'A-' | 'B+' | 'B-' | 'AB+' | 'AB-' | 'O+' | 'O-';
  allergies?: string;
  chronic_conditions?: string;
  current_medications?: string;
  medical_history?: string;
  mrn?: string;
  doctor_id: string;
  clinic_id: string;
  status?: 'Active' | 'Inactive' | 'Archived';
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  date_of_birth?: string;
  payment_preference?: 'Cash' | 'Insurance' | 'Card' | 'UPI' | 'Other';
  created_at: string;
  updated_at: string;
  /** Kept by the server on every visit, including ones without a prescription */
  last_visit_date?: string | null;
}

interface Visit {
  id: string;
  date: string;
  prescription?: Prescription;
  appointment?: any;
}

interface MedicalAlert {
  type: 'allergy' | 'condition';
  text: string;
  details: string;
}

interface FollowUpStatus {
  status: string;
  isOverdue: boolean;
  message: string;
}

export const usePatientData = (patientId: string) => {
  const { currentUser } = useAuth();
  
  // State management
  const [patient, setPatient] = useState<Patient | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  // Real visits (status, readings, documents); `visits` above stays one entry per prescription
  const [visitRecords, setVisitRecords] = useState<VisitRecord[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch patient data using the correct API
  const fetchPatientData = useCallback(async () => {
    if (!patientId) return;
    
    setLoading(true);
    try {
      const data = await getProfile(patientId);
      
      if (data?.patient) {
        setPatient(data.patient);
        
        setVisitRecords(Array.isArray(data.visitRecords) ? data.visitRecords : []);

        // Process visits with prescriptions
        if (Array.isArray(data.visits)) {
          const processedVisits: Visit[] = data.visits
            .filter((visit: any) => visit && visit.prescription)
            .map((visit: any) => ({
              id: visit.id || `visit-${Date.now()}-${Math.random()}`,
              date: visit.date || visit.prescription?.date || new Date().toISOString(),
              prescription: visit.prescription ? {
                id: visit.prescription.id,
                patient_id: patientId,
                doctor_id: visit.prescription.doctor_id || currentUser?.doctor_id || "",
                clinic_id: visit.prescription.clinic_id || currentUser?.clinic_id || "",
                date: visit.prescription.date || visit.date,
                diagnosis: visit.prescription.diagnosis || "",
                examination: visit.prescription.examination || "",
                complaints: Array.isArray(visit.prescription.complaints) ? visit.prescription.complaints : [],
                medicines: Array.isArray(visit.prescription.medicines) ? visit.prescription.medicines : [],
                investigations: Array.isArray(visit.prescription.investigations) ? visit.prescription.investigations : [],
                advice: visit.prescription.advice || "",
                quick_notes: visit.prescription.quick_notes || "",
                vitals: visit.prescription.vitals || {},
                next_visit: visit.prescription.next_visit || {},
                custom_fields: visit.prescription.custom_fields || {},
                created_at: visit.prescription.created_at,
                updated_at: visit.prescription.updated_at
              } : undefined
            }))
            // Newest first; prescriptions of one day (the date has no time) by when they were saved
            .sort((a: Visit, b: Visit) => new Date(b.date).getTime() - new Date(a.date).getTime()
              || new Date(b.prescription?.created_at ?? 0).getTime() - new Date(a.prescription?.created_at ?? 0).getTime());
          
          setVisits(processedVisits);
        } else {
          setVisits([]);
        }

        // Process appointments
        if (Array.isArray(data.appointments)) {
          const processedAppointments: Appointment[] = data.appointments.map((appointment: any) => ({
            id: appointment.id,
            patient_id: appointment.patient_id || patientId,
            doctor_id: appointment.doctor_id || currentUser?.doctor_id || "",
            clinic_id: appointment.clinic_id || currentUser?.clinic_id || "",
            date: appointment.date,
            time: appointment.time,
            reason: appointment.reason || appointment.notes || "General Appointment",
            status: appointment.status || 'scheduled',
            created_at: appointment.created_at || new Date().toISOString(),
            updated_at: appointment.updated_at || new Date().toISOString(),
            last_modified: appointment.last_modified || new Date().toISOString(),
            synced_at: appointment.synced_at || null,
            patient_name: data.patient?.name || "",
            doctor_name: appointment.doctor_name || "",
            clinic_name: appointment.clinic_name || ""
          }));
          
          processedAppointments.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
          setAppointments(processedAppointments);
        } else {
          setAppointments([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch patient data:', error);
      toast({
        title: "Error",
        description: "Failed to load patient data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [patientId, currentUser?.doctor_id, currentUser?.clinic_id]);

  useEffect(() => {
    fetchPatientData();
  }, [fetchPatientData]);

  // Utility functions
  const getDaysAgo = useCallback((dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return `${Math.floor(diffDays / 30)} months ago`;
    } catch {
      return 'Unknown';
    }
  }, []);

  const getBPStatus = useCallback((bp: string) => {
    const match = bp.match(/(\d+)\/(\d+)/);
    if (!match) return 'normal';
    
    const systolic = parseInt(match[1]);
    const diastolic = parseInt(match[2]);
    
    if (systolic >= 140 || diastolic >= 90) return 'high';
    if (systolic >= 120 || diastolic >= 80) return 'elevated';
    return 'normal';
  }, []);

  // Computed values
  const latestBP = useMemo(() => {
    if (visits.length === 0) return 'No Data';
    const latestBP = visits[0].prescription?.vitals?.bp;
    if (!latestBP) return 'No Data';
    return latestBP;
  }, [visits]);

  const latestBPStatus = useMemo(() => {
    if (visits.length === 0) return 'normal';
    const latestBP = visits[0].prescription?.vitals?.bp;
    if (!latestBP) return 'normal';
    return getBPStatus(latestBP);
  }, [visits, getBPStatus]);

  const followUpStatus = useMemo((): FollowUpStatus => {
    if (visits.length === 0) {
      return {
        status: 'No Data',
        isOverdue: false,
        message: 'No visits recorded'
      };
    }
    
    const latestBP = visits[0].prescription?.vitals?.bp;
    if (!latestBP) {
      return {
        status: 'Normal',
        isOverdue: false,
        message: 'No BP data'
      };
    }
    
    const bpStatus = getBPStatus(latestBP);
    const lastVisitDate = new Date(visits[0].date);
    const daysSinceLastVisit = Math.floor((new Date().getTime() - lastVisitDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (bpStatus === 'high') {
      return {
        status: 'High Risk',
        isOverdue: daysSinceLastVisit > 7,
        message: daysSinceLastVisit > 7 ? 'Urgent follow-up needed' : 'Monitor closely'
      };
    }
    
    if (bpStatus === 'elevated') {
      return {
        status: 'Monitor',
        isOverdue: daysSinceLastVisit > 14,
        message: daysSinceLastVisit > 14 ? 'Follow-up recommended' : 'Regular monitoring'
      };
    }
    
    return {
      status: 'Normal',
      isOverdue: daysSinceLastVisit > 30,
      message: daysSinceLastVisit > 30 ? 'Routine check-up due' : 'All good'
    };
  }, [visits, getBPStatus]);

  const medicalAlerts = useMemo((): MedicalAlert[] => {
    if (!patient) return [];
    const alerts = [];
    if (patient.allergies) alerts.push({ type: 'allergy' as const, text: 'Allergies', details: patient.allergies });
    if (patient.chronic_conditions) alerts.push({ type: 'condition' as const, text: 'Chronic', details: patient.chronic_conditions });
    return alerts;
  }, [patient]);

  return {
    // Data
    patient,
    visits,
    visitRecords,
    appointments,
    loading,
    
    // Computed values
    latestBP,
    latestBPStatus,
    followUpStatus,
    medicalAlerts,
    
    // Utilities
    getDaysAgo,
    getBPStatus,
    
    // Actions
    fetchPatientData
  };
}; 